import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

// Traduções
import translations from './dictionary/translateLogin';
import { adTranslations } from './dictionary/translateAdSimulator';
import { languages } from './LanguageSelector';
import LanguageSelector from './LanguageSelector';
import CountDownTimer from './CountDownTimer';

// IMPORT do novo CSS renomeado
import './styles/Login.css';

function Login({ onLogin }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [pageLanguage, setPageLanguage] = useState('pt');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resetToken, setResetToken] = useState('');

  // Controle de anúncio
  const [showAd, setShowAd] = useState(false);
  const isSimulation = process.env.REACT_APP_AD_SIMULATION === 'true';

  // Controle de áudio
  const audioRef = useRef(null);
  const [isMuted, setIsMuted] = useState(false);

  // Navigation + location
  const navigate = useNavigate();
  const location = useLocation();

  // Traduções
  const t = translations[pageLanguage] || translations.en;
  const s = adTranslations[pageLanguage] || adTranslations.en;

  // Data alvo do countdown
  const targetDate = new Date('2025-05-21T19:00:00');

  // Limpa mensagens após 3s
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError('');
        setSuccess('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Pega token do URL (reset password)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get('token');
    if (tokenFromUrl) {
      setResetToken(tokenFromUrl);
      setUsername(tokenFromUrl);
    }
  }, [location.search]);

  // --------------------------------------------------
  // Lógica de exibir anúncio e, depois que carrega + 5s,
  // navegar ou chamar callback (similar ao PlanSelection)
  // --------------------------------------------------
  const showAdAndThen = (callback) => {
    setShowAd(true);

    if (isSimulation) {
      // Em simulação, exibe 5s e chama callback
      setTimeout(() => {
        setShowAd(false);
        if (typeof callback === 'function') {
          callback();
        }
      }, 5000);
    } else {
      // Produção: aguarda o anúncio "carregar" (offsetHeight > 0) e +5s
      const checkAdLoaded = () => {
        const adElement = document.querySelector('.adsbygoogle');
        if (adElement && adElement.offsetHeight > 0) {
          // Aguarda 5s
          setTimeout(() => {
            setShowAd(false);
            if (typeof callback === 'function') {
              callback();
            }
          }, 5000);
        } else {
          setTimeout(checkAdLoaded, 500);
        }
      };
      checkAdLoaded();
    }
  };

  // Exibir anúncio antes de navegar
  const showAdAndNavigate = (navigatePath, planStatus, userData) => {
    showAdAndThen(() => {
      if (planStatus === 'active') onLogin('active');
      if (planStatus === 'pending') onLogin('pending');

      navigate(navigatePath, {
        state: { selectedLanguage: pageLanguage, user: userData },
      });
    });
  };

  // Carrega anúncio se showAd=true e NÃO está em simulação
  useEffect(() => {
    if (showAd && !isSimulation) {
      window.adsbygoogle = window.adsbygoogle || [];
      window.adsbygoogle.push({});
    }
  }, [showAd, isSimulation]);

  // -------------------------------------------
  // Login ou reset de senha
  // -------------------------------------------
  const handleLogin = async () => {
    setError('');
    setSuccess('');

    if (password.length < 6) {
      setError(t.passwordTooShort);
      return;
    }

    // Se existe resetToken, redefinindo senha
    if (resetToken) {
      try {
        await axios.post(
          `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/users/reset-password`,
          {
            token: username,
            newPassword: password,
          }
        );
        setSuccess(t.tokenResetSuccess);
        setTimeout(() => {
          navigate('/');
        }, 1500);
        setResetToken('');
        setUsername('');
        setPassword('');
        return;
      } catch (err) {
        setError(t.tokenResetError);
        return;
      }
    }

    // Fluxo normal de login
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_VISION_AI_STREAM_API_URL}/api/users/auth`,
        {
          email: username,
          password
        }
      );

      const { user } = response.data;
      sessionStorage.setItem('user', JSON.stringify({ ...user, password }));
      sessionStorage.setItem('pageLanguage', pageLanguage);

      // Status do plano
      if (response.status === 201) {
        // plano pendente
        onLogin('pending');
        navigate('/plan-selection');
      } else if (user.planDetails?.status === 'active') {
        // Se for Vision Free => exibe anúncio antes de ir ao editor
        if (user.planDetails?.planType === 'Vision Free') {
          showAdAndNavigate('/video-editor', 'active', user);
        } else {
          // Se for outro plano
          onLogin('active');
          navigate('/video-editor', {
            state: { selectedLanguage: pageLanguage, user },
          });
        }
      } else if (user.planDetails?.status === 'pending') {
        onLogin('pending');
        navigate('/plan-selection');
      } else {
        setError(t.planStatusInvalid);
      }
    } catch (error) {
      if (error.response) {
        const { status, data } = error.response;
        if (status === 401) {
          sessionStorage.setItem('lastTypedEmail', username);
          setError(t.incorrectPasswordEmailLink);
        } else if (status === 403 && data.errorCode === 'BLOCKED_NEW_USERS') {
          setError(
            <span style={{ color: '#ff7600', fontWeight: 'bold' }}>
              {t.blockedUsersMessage}
            </span>
          );
        } else {
          setError(t.loginError);
        }
      } else {
        setError(t.loginError);
      }
    }
  };

  // ENTER para logar
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleLogin();
    }
  };

  // Áudio: mute/unmute
  const toggleMuteState = () => {
    setIsMuted(!isMuted);
    if (audioRef.current) {
      if (!isMuted) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } else {
        audioRef.current.play().catch((err) => {
          console.error('Falha ao tocar o áudio:', err);
        });
      }
    }
  };

  // Tocar áudio ao montar
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.play().catch((err) => {
        console.error('Falha ao tocar o áudio automaticamente:', err);
      });
    }
  }, []);

  return (
    <div className="login-editor" onKeyDown={handleKeyDown} tabIndex="0">
      {/* Timer regressivo */}
      <CountDownTimer targetDate={targetDate} pageLanguage={pageLanguage} />

      {/* Ícone de volume */}
      <div onClick={toggleMuteState} style={{ cursor: 'pointer', marginBottom: '10px' }}>
        <i
          className={`fas ${isMuted ? 'fa-volume-mute' : 'fa-volume-up'}`}
          style={{ fontSize: '24px', color: 'white' }}
        ></i>
      </div>

      {/* Botão Sobre Nós movido para abaixo do contador */}
      <button
        className="about-button"
        onClick={() => navigate('/about', { state: { selectedLanguage: pageLanguage } })}
      >
        {t.aboutUS}
      </button>

      {/* Áudio (invisível) */}
      <audio ref={audioRef} style={{ display: 'none' }} loop>
        <source
          src={process.env.REACT_APP_VISION_AI_STREAM_AUDIO_PATH}
          type="audio/mpeg"
        />
        Your browser does not support the audio element.
      </audio>

     {/* Mensagem de destaque com espaçamento e cor ajustados */}
      <div className="login-highlight">
          <span className="login-test">
              {t.opportunityMessage}
          </span>

          <span className="login-message">{t.enterEmailMessage}</span>
          <span className="login-arrow">⬇️</span>
      </div>

      {/* Inputs de login ou reset */}
      <input
        type="text"
        value={username}
        onChange={(e) => setUsername(e.target.value)}
        autoComplete="off"
        placeholder={resetToken ? t.tokenPlaceholder : t.usernameOrEmailPlaceholder}
        className="login-input"
      />

      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder={resetToken ? t.newPasswordPlaceholder : t.passwordPlaceholder}
        className="login-input"
      />

      <div className="login-highlight">
          <span className="login-arrow">⬆️</span>
          <span className="login-message">{t.enterPasswordMessage}</span>
      </div>

      {/* Seletor de idioma */}
      <div className="language-section">
        <LanguageSelector
          selectedLanguage={pageLanguage}
          onLanguageChange={setPageLanguage}
          label={
            <span
              style={{
                color: '#fff',
                fontWeight: 'bold',
                textAlign: 'center',
                display: 'block',
              }}
            >
              {t.pageLanguageLabel}
            </span>
          }
          options={languages.filter((lang) => lang.code !== 'auto')}
        />
      </div>

      {/* Botão principal */}
      <button
        className="custom-button"
        onClick={handleLogin}
        title={t.loginButtonTooltip}
      >
        {resetToken ? t.resetPasswordButton : t.loginButton}
      </button>

      {/* Mensagens de erro e sucesso */}
      {error && (
        <div style={{ color: 'red', marginTop: '10px' }}>
          {error}
        </div>
      )}
      {success && (
        <div style={{ color: 'green', marginTop: '10px' }}>
          {success}
        </div>
      )}

      {/* Anúncio se showAd = true */}
      {showAd && (
        <div style={{ marginTop: '20px', textAlign: 'center' }}>
          {isSimulation ? (
            // Simulação local (cinza, 5s)
            <div
              style={{
                padding: '20px',
                border: '1px solid #ccc',
                backgroundColor: '#f9f9f9',
              }}
            >
              <p>{s.adSimulation}</p>
            </div>
          ) : (
            // Produção: anúncio real fixo com fundo branco p/ evitar "preto"
            <>
              {/* Se já tiver o script no index.html, pode remover daqui */}
              <script
                async
                src="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-6453889750420507"
                crossOrigin="anonymous"
              ></script>

              <ins
                className="adsbygoogle"
                style={{
                  display: 'inline-block',
                  width: '336px',
                  height: '280px',
                  backgroundColor: '#fff',
                  border: '1px solid #ccc',
                }}
                data-ad-client="ca-pub-6453889750420507"
                data-ad-slot="1234567890"
              ></ins>

              <script>{`
                (adsbygoogle = window.adsbygoogle || []).push({});
              `}</script>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Login;
